import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiOutlineStop, HiSparkles } from "react-icons/hi";
import { IoSend } from "react-icons/io5";
import { classNames } from "Utils/utils";
import { VERTEX360_COPILOT_RESPONDING_LABEL } from "../constants/vertex360CopilotConstants";
import { VERTEX360_COPILOT_THEME } from "../constants/vertex360CopilotTheme";
import ActiveFiltersBar from "./ActiveFiltersBar";

const MessageComposer = ({
  onSend,
  onStop,
  disabled = false,
  isSending = false,
  focusRequestKey = 0,
  activeFilters = null,
  contextSummary = null,
  onClearFilters,
}) => {
  const [value, setValue] = useState("");
  const textareaRef = useRef(null);

  const canSend = value.trim().length > 0 && !disabled && !isSending;
  const inputLocked = disabled || isSending;

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [value, resizeTextarea]);

  useEffect(() => {
    if (!focusRequestKey) return undefined;

    const frame = requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [focusRequestKey]);

  const handleSend = useCallback(async () => {
    if (!canSend) return;
    const text = value.trim();
    setValue("");
    await onSend?.(text);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [canSend, onSend, value]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (isSending) return;
      handleSend();
    }
  };

  return (
    <div
      className={classNames(
        "relative z-[2] shrink-0 px-4 pb-4 pt-2",
        VERTEX360_COPILOT_THEME.composerShell,
        "border-t"
      )}
    >
      <ActiveFiltersBar
        activeFilters={activeFilters}
        contextSummary={contextSummary}
        onClear={onClearFilters}
        disabled={disabled || isSending}
      />

      {/* {isSending ? (
        <div
          className="copilot-composer-status mb-2 flex items-center justify-center"
          role="status"
          aria-live="polite"
          aria-label={VERTEX360_COPILOT_RESPONDING_LABEL}
        >
          <span className="copilot-composer-status-pill inline-flex items-center gap-2 rounded-full border border-[#C7D7F5]/90 bg-[#ECF0FD]/70 px-3 py-1">
            <span
              className="copilot-thinking-dots copilot-thinking-dots--sm"
              aria-hidden="true"
            >
              <span />
              <span />
              <span />
            </span>
            <span className="text-[11.5px] font-medium text-[#293F6A]">
              Working on your answer
            </span>
            <span className="text-[10.5px] text-slate-400">
              · <span className="font-semibold text-slate-500">Esc</span> to stop
            </span>
          </span>
        </div>
      ) : null} */}

      <div
        className={classNames(
          "flex items-end gap-2 rounded-2xl px-3 py-1 transition-all duration-200",
          VERTEX360_COPILOT_THEME.composerInput
        )}
      >
        <span className="mb-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#ECF0FD] text-[#4B6CB7]">
          <HiSparkles className="h-4 w-4" />
        </span>

        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          readOnly={isSending}
          disabled={disabled}
          placeholder="Ask anything about your organisation…"
          className={classNames(
            "min-h-[36px] max-h-[160px] flex-1 resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-[14px] leading-relaxed text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-0",
            inputLocked && "cursor-not-allowed",
            disabled && "opacity-60"
          )}
          aria-label="Message Vertex360 Copilot"
          aria-busy={isSending}
        />

        {isSending ? (
          <button
            type="button"
            onClick={onStop}
            className="mb-0.5 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            aria-label="Stop generating"
          >
            <HiOutlineStop className="h-4 w-4" />
            Stop
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className={classNames(
              "mb-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all duration-200",
              canSend
                ? "bg-gradient-to-br from-[#4B6CB7] to-[#293F6A] text-white shadow-lg shadow-[#4B6CB7]/30 hover:brightness-110 active:scale-95"
                : "cursor-not-allowed bg-slate-100 text-slate-400"
            )}
            aria-label="Send message"
          >
            <IoSend className="h-[16px] w-[16px] translate-x-[0.5px]" />
          </button>
        )}
      </div>

      <p className="mt-2 text-center text-[11px] leading-snug text-slate-400">
        Vertex360 Copilot can make mistakes. Always verify important details.
      </p>
      <p className="mt-1 text-center text-[10.5px] text-slate-400">
        <span className="font-semibold text-slate-500">Enter</span> to send ·{" "}
        <span className="font-semibold text-slate-500">Shift + Enter</span> for new line
      </p>
    </div>
  );
};

export default MessageComposer;
